import { Stack, Typography, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import { VyIcon, VyIconType } from '@vy/ui-design-system';
import { VyDivider } from '../../../../../../../../../components/new-design/atoms';
import type { GetExportedIntegrationsResponse, GetLightInvoicesResponse } from '@vy/api-definition';
import type { ItemOf, Optional } from '@vy/utils';
import { isNotEmptyString, raiseUnreachable, withDefault, now } from '@vy/utils';
import { INTEGRATION_TYPES, SYNC_JOB_STATUSES } from '@vy/integrations-types';
import type { SyncJobStatus } from '@vy/integrations-types';
import { isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import { useToggle } from '../../../../../../../../../hooks/use-toggle';
import { formatDate } from '../../../../../../../../../utils/date';
import { runMutation } from '../../../../../../../../../utils/vy-mutation';
import { usePaddleExportInvoices } from '../../../../../../../../../hooks/requests/integrations';
import { useGetPaddleTransactionUrl } from '../../../../../../../../../hooks/requests/use-get-paddle-transaction-url';
import { toastFromPromise } from '../../../../../../../../../utils/vy-toasts';
import { useQueryClient } from 'react-query';
import { queryKeys } from '../../../../../../../../../services/query-key/query-keys';
import { PaddleInvoiceProductsAccordion } from './invoice-activity-paddle-sync-tab-components/PaddleInvoiceProductsAccordion/PaddleInvoiceProductsAccordion';

type InvoiceActivityPaddleSyncTabProps = {
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
  exportedInvoiceIntegrations: GetExportedIntegrationsResponse;
};

export const InvoiceActivityPaddleSyncTab: React.FC<InvoiceActivityPaddleSyncTabProps> = ({
  invoice,
  exportedInvoiceIntegrations,
}) => {
  const paddleIntegration = exportedInvoiceIntegrations.find(
    (exportedIntegration) => exportedIntegration.integration === INTEGRATION_TYPES.PADDLE,
  );
  const isExported = paddleIntegration?.status === SYNC_JOB_STATUSES.COMPLETED;

  return (
    <Stack
      spacing="12px"
      divider={<VyDivider />}
      sx={{
        height: 'calc(100vh - 400px)',
        overflowY: 'auto',
        scrollbarWidth: 'none',
      }}
    >
      <PaddleIntegrationStatusAccordion paddleIntegration={paddleIntegration} invoice={invoice} />
      <PaddleInvoiceProductsAccordion invoiceId={invoice.id} isExported={isExported} />
    </Stack>
  );
};

type PaddleIntegrationStatusAccordionProps = {
  paddleIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

const PaddleIntegrationStatusAccordion: React.FC<PaddleIntegrationStatusAccordionProps> = ({
  paddleIntegration,
  invoice,
}) => {
  const [expanded, toggleExpanded] = useToggle(true);
  const exportToPaddleMutation = usePaddleExportInvoices();
  const queryClient = useQueryClient();
  const { refetch: fetchPaddleTransactionUrl } = useGetPaddleTransactionUrl(invoice.id, {
    enabled: false,
  });

  const isSynced = paddleIntegration?.status === SYNC_JOB_STATUSES.COMPLETED;
  const isApproved = invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const isSyncable = isInvoiceSyncToIntegrationAllowed(invoice);
  const status = withDefault(paddleIntegration?.status, SYNC_JOB_STATUSES.PENDING);
  const label = getLabel(status);
  const icon = getIcon(status);

  const getStatusText = () => {
    if (isSynced) {
      return formatDate(paddleIntegration?.updatedAt ?? now());
    }
    if (!isApproved) {
      return 'Approve invoice to enable syncing';
    }
    if (!isSyncable) {
      return 'Child invoices sync through the parent customer';
    }
    return formatDate(paddleIntegration?.updatedAt ?? now());
  };

  const handleOpenOnPaddle = async () => {
    await toastFromPromise(
      (async () => {
        const { data } = await fetchPaddleTransactionUrl({ throwOnError: true });
        const paddleUrl = data?.url;

        if (!isNotEmptyString(paddleUrl)) {
          throw new Error('Paddle transaction URL is unavailable.');
        }

        window.open(paddleUrl, '_blank');
      })(),
      {
        pending: 'Opening Paddle transaction...',
        success: 'Opened',
        error: 'Failed to open Paddle transaction',
      },
    );
  };

  const handleSyncToPaddle = async () => {
    await runMutation(
      exportToPaddleMutation,
      { invoiceIds: [invoice.id] },
      {
        pending: 'Requesting sync to Paddle...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to Paddle',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.invoice(invoice.id)),
            queryClient.invalidateQueries(queryKeys.exportedInvoiceIntegrations(invoice.id)),
          ]);
        },
      },
    );
  };

  return (
    <Accordion expanded={expanded} onChange={() => toggleExpanded()} sx={{ p: 0 }}>
      <AccordionSummary
        expandIcon={<VyIcon icon="ArrowDown" style={{ color: '#323232' }} />}
        sx={{ p: 0, height: '34px', alignItems: 'center' }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <VyIcon icon="Synchronize" />
          <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: '24px' }}>
            Integration status
          </Typography>
          {isSynced && (
            <div
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#65CF7E',
                borderRadius: '50%',
              }}
            />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <Stack sx={{ gap: 2, mb: 1.5, mt: 1.5 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack sx={{ alignItems: 'flex-start', gap: '4px' }}>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                <VyIcon icon={icon} strokeOpacity={0} />
                <Typography sx={{ fontSize: 14, lineHeight: '20px' }}>
                  {label}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 14, lineHeight: '20px', color: '#7E8796' }}>
                {getStatusText()}
              </Typography>
            </Stack>

            {!isSynced && (
              <Button
                variant="text"
                size="medium"
                startIcon={<VyIcon icon="Synchronize" transform="scale(0.7)" />}
                onClick={handleSyncToPaddle}
                disabled={!isSyncable}
              >
                Sync
              </Button>
            )}
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            size="large"
            endIcon={<VyIcon icon="OpenNew" />}
            onClick={handleOpenOnPaddle}
            disabled={!isSynced}
          >
            Open on Paddle
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

function getLabel(status: SyncJobStatus): string {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'Paddle not synced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'Sync failed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'Synced to Paddle';
    default:
      return raiseUnreachable(status);
  }
}

function getIcon(status: SyncJobStatus): VyIconType {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'PaddleNotSynced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'PaddleSyncFailed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'PaddleSynced';
    default:
      return raiseUnreachable(status);
  }
}
